/**
 * Calculates the due date based on a start date and a number of days
 * @param startDate The start date
 * @param dueAfter Number of days after which the assignment is due
 * @returns The due date
 */
export const getDueDate = (startDate: Date | null, dueAfter: number): Date | null => {
  if (!startDate) return null;

  const dueDate = new Date(startDate);
  dueDate.setDate(dueDate.getDate() + dueAfter);
  dueDate.setTime(dueDate.getTime() - 1); // retrieves ms, subtracts 1, sets ms
  return dueDate;
};

export const formatShortDate = (dateString: string | number | Date) => {
  const date = new Date(dateString);

  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear().toString();

  return `${month}.${day}.${year}`;
};

export const formatTimeToComplete = (timeString: string) => {
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return `${hours}h ${minutes}m ${seconds}s`;
};

export function formatTotalTime(seconds: number) {
  if (!seconds || isNaN(seconds)) return "0m";

  const days = Math.floor(seconds / (60 * 60 * 24));
  const hours = Math.floor((seconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((seconds % (60 * 60)) / 60);

  const parts = [];
  if (days > 0) parts.push(`${days}D`);
  if (hours > 0) parts.push(`${hours}H`);
  if (minutes > 0) parts.push(`${minutes}M`);

  return parts.length > 0 ? parts.join(" ") : "0M";
}

export function formatElapsedTime(seconds: number) {
  if (!seconds || isNaN(seconds)) return "0s";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  parts.push(`${secs}s`);

  return parts.length > 0 ? parts.join(" ") : "0s";
}

export function areDatesOnDifferentDays(date1: Date, date2: Date) {
  return (
    date1.getFullYear() !== date2.getFullYear() ||
    date1.getMonth() !== date2.getMonth() ||
    date1.getDate() !== date2.getDate()
  );
}

/**
 * Calculates the calendar difference between two dates, ignoring the time component.
 * @param date1 - The first date
 * @param date2 - The second date
 * @returns An object with weeks and days properties representing the calendar difference
 */
export function getCalendarDiff(date1: Date, date2: Date): { weeks: number; days: number } {
  // Get the start of both dates (midnight)
  const start1 = new Date(date1);
  start1.setHours(0, 0, 0, 0);

  const start2 = new Date(date2);
  start2.setHours(0, 0, 0, 0);

  // Calculate the difference in calendar days
  const timeDiff = Math.abs(start1.getTime() - start2.getTime());
  const totalDays = Math.floor(timeDiff / (1000 * 3600 * 24));

  // Break down into weeks and remaining days
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;

  return { weeks, days };
}

/**
 * Calculates the number of calendar days ago from today.
 * @param pastDate - The past date to compare to today
 * @returns The number of calendar days ago
 */
export function getDaysAgo(pastDate: Date): number {
  const currentDate = new Date();
  const diff = getCalendarDiff(currentDate, pastDate);
  return diff.weeks * 7 + diff.days;
}

/**
 * Gets the start date of the week (Sunday at midnight) for a given date.
 * @param {Date} date - The date to check.
 * @returns {Date} The start date of the week.
 */
function getStartOfWeek(date: Date) {
  // Create a new date object to avoid modifying the original
  const startOfWeek = new Date(date);
  // Set time to midnight (00:00:00)
  startOfWeek.setHours(0, 0, 0, 0);
  // Get the current day of the week (0 is Sunday, 6 is Saturday)
  const dayOfWeek = startOfWeek.getDay();
  // Subtract the number of days to get to the previous Sunday
  startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
  return startOfWeek;
}

/**
 * Checks if two date objects are in different calendar weeks.
 * @param {Date} date1 - The first date.
 * @param {Date} date2 - The second date.
 * @returns {boolean} True if the dates are in different weeks, false otherwise.
 */
export function areDatesInDifferentWeeks(date1: Date, date2: Date) {
  const startOfWeek1 = getStartOfWeek(date1);
  const startOfWeek2 = getStartOfWeek(date2);

  // Compare the start dates of their respective weeks
  return startOfWeek1.getTime() !== startOfWeek2.getTime();
}
